import type { IntakeRecord } from '../store/slices/intakeSlice';
import type { AgencyClinicResponse } from '../services/api/types.gen';
import { formatDateToZeroPaddedMMDDYYYY } from './dateUtils';

type RowIntake = IntakeRecord & {
  fields?: Record<string, unknown>;
  coding_extraction?: Record<string, unknown> | null;
  tab_statuses?: Record<string, string | undefined> | null;
  soc_date?: string | null;
  discharge_date?: string | null;
  agency_clinic?: AgencyClinicResponse | null;
};

function fieldsGet(intake: RowIntake, key: string): unknown {
  const fromFields = intake.fields?.[key];
  if (fromFields !== undefined && fromFields !== null && fromFields !== '') {
    return fromFields;
  }
  const direct = (intake as unknown as Record<string, unknown>)[key];
  return direct ?? '';
}

function listJoin(val: unknown): string {
  if (Array.isArray(val)) return val.map(String).join(', ');
  return val != null && val !== '' ? String(val) : '';
}

function boolLabel(val: boolean | undefined): string {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '';
}

const TAB_STATUS_LABELS: Record<string, string> = {
  complete: 'Complete',
  complete_with_errors: 'Complete (errors)',
  skipped: 'Skipped',
  incomplete: 'Incomplete',
};

function tabStatusLabel(intake: RowIntake, tabKey: string): string {
  const ts = intake.tab_statuses ?? {};
  const val = ts[tabKey];
  if (val == null || val === '') return '';
  return TAB_STATUS_LABELS[val] ?? String(val);
}

const OVERRIDE_KEY_MAP: Record<string, string> = {
  home_health_orders_attached: 'order_attached',
  home_health_orders_signed: 'order_signed',
  face_to_face_visits_attached: 'f2f_attached',
  face_to_face_visits_signed: 'f2f_signed',
};

function codingExtractionStatus(
  intake: RowIntake,
  key: 'home_health_orders' | 'face_to_face_visits',
  check: 'attached' | 'signed'
): string {
  const fields = intake.fields ?? {};
  const overrides =
    (fields.document_validation_manual_overrides as Record<
      string,
      { value?: boolean } | unknown
    > | null) ?? {};
  const overrideKey = OVERRIDE_KEY_MAP[`${key}_${check}`] ?? '';
  if (overrideKey && overrides[overrideKey] != null) {
    const o = overrides[overrideKey] as { value?: boolean };
    if (
      o &&
      typeof o === 'object' &&
      o.value !== undefined &&
      o.value !== null
    ) {
      return o.value ? 'Yes' : 'No';
    }
  }

  const ce = intake.coding_extraction ?? {};
  const raw = ce[key];
  if (!Array.isArray(raw)) return '';

  if (check === 'attached') {
    return raw.length > 0 ? 'Yes' : 'No';
  }

  for (const item of raw) {
    if (item && typeof item === 'object') {
      const validations = (item as { validations?: Record<string, unknown> })
        .validations;
      const isSigned = validations?.is_signed as
        | { value?: boolean }
        | undefined;
      if (isSigned?.value === true) return 'Yes';
    }
  }
  return raw.length > 0 ? 'No' : '';
}

function formatDateish(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') {
    // Normalize ISO-ish date/datetime strings to MM/DD/YYYY for display.
    // - "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss..." -> MM/DD/YYYY
    // - already-formatted strings (e.g. "N/A") pass through
    const mmddyyyy = formatDateToZeroPaddedMMDDYYYY(v);
    return mmddyyyy || v;
  }
  return String(v);
}

/**
 * Plain-text cell value for a catalog column id (not used for composite patient/status/insurance/assignee).
 */
export function getIntakeManagerColumnDisplayString(
  intake: IntakeRecord,
  columnId: string
): string {
  const i = intake as RowIntake;

  switch (columnId) {
    case 'patient_first_name':
      return i.patient_first_name ?? '';
    case 'patient_last_name':
      return i.patient_last_name ?? '';
    case 'patient_middle_name':
      return i.patient_middle_name ?? '';
    case 'patient_dob':
      return formatDateish(i.patient_dob);
    case 'patient_gender':
      return i.patient_gender != null ? String(i.patient_gender) : '';
    case 'patient_ssn':
      return i.patient_ssn ?? '';
    case 'patient_phone':
      return i.patient_phone ?? '';
    case 'patient_secondary_phone':
      return i.patient_secondary_phone ?? '';
    case 'patient_email':
      return i.patient_email ?? '';
    case 'street_address':
      return i.patient_street_address ?? '';
    case 'city':
      return i.patient_city ?? '';
    case 'state':
      return i.patient_state ?? '';
    case 'zip_code':
      return i.patient_zip_code ?? '';
    case 'county':
      return String(fieldsGet(i, 'patient_county') ?? '');
    case 'primary_language':
      return String(fieldsGet(i, 'patient_primary_language') ?? '');
    case 'race':
      return String(fieldsGet(i, 'patient_race') ?? '');
    case 'ethnicity':
      return String(fieldsGet(i, 'patient_ethnicity') ?? '');
    case 'height':
      return String(fieldsGet(i, 'patient_height') ?? '');
    case 'weight':
      return String(fieldsGet(i, 'patient_weight') ?? '');
    case 'medicaid_id':
      return String(fieldsGet(i, 'medicaid_id_number') ?? '');
    case 'emergency_contact_name':
      return String(fieldsGet(i, 'emergency_contact_name') ?? '');
    case 'emergency_contact_relationship':
      return String(fieldsGet(i, 'emergency_contact_relationship') ?? '');
    case 'emergency_contact_phone':
      return String(fieldsGet(i, 'emergency_contact_phone') ?? '');
    case 'insurance_mapped':
      return i.agency_insurance?.name ?? '';
    case 'primary_insurance':
      return String(
        fieldsGet(i, 'primary_insurance') ?? i.primary_insurance ?? ''
      );
    case 'primary_member_id':
      return String(
        fieldsGet(i, 'primary_member_id') ?? i.primary_member_id ?? ''
      );
    case 'secondary_insurance':
      return String(
        fieldsGet(i, 'secondary_insurance') ?? i.secondary_insurance ?? ''
      );
    case 'secondary_member_id':
      return String(
        fieldsGet(i, 'secondary_member_id') ?? i.secondary_member_id ?? ''
      );
    case 'referring_provider':
      return i.referring_physician ?? '';
    case 'referring_phone':
      return String(fieldsGet(i, 'referring_phone') ?? i.referring_phone ?? '');
    case 'referring_fax':
      return String(fieldsGet(i, 'referring_fax') ?? i.referring_fax ?? '');
    case 'referral_source':
      return i.referral_source ?? '';
    case 'discharge_facility':
      return String(
        fieldsGet(i, 'discharge_facility_name') ??
          i.discharge_facility_name ??
          ''
      );
    case 'discharge_facility_type':
      return String(
        fieldsGet(i, 'discharge_facility_type') ??
          i.discharge_facility_type ??
          ''
      );
    case 'f2f_physician':
      return String(fieldsGet(i, 'f2f_physician') ?? '');
    case 'primary_diagnosis':
      return String(
        fieldsGet(i, 'primary_diagnosis') ?? i.primary_diagnosis ?? ''
      );
    case 'diagnosis_codes':
      return listJoin(fieldsGet(i, 'diagnosis_codes') ?? i.diagnosis_codes);
    case 'services_requested':
      return listJoin(
        fieldsGet(i, 'services_requested') ?? i.services_requested
      );
    case 'allergies':
      return String(fieldsGet(i, 'allergies') ?? '');
    case 'past_medical_history':
      return String(fieldsGet(i, 'past_medical_history') ?? '');
    case 'homebound_reason':
      return String(fieldsGet(i, 'homebound_reason') ?? '');
    case 'physician_order_date':
      return formatDateish(i.physician_order_date);
    case 'order_attached':
      return codingExtractionStatus(i, 'home_health_orders', 'attached');
    case 'order_signed':
      return codingExtractionStatus(i, 'home_health_orders', 'signed');
    case 'f2f_encounter_date':
      return formatDateish(i.f2f_encounter_date);
    case 'f2f_attached':
      return codingExtractionStatus(i, 'face_to_face_visits', 'attached');
    case 'f2f_signed':
      return codingExtractionStatus(i, 'face_to_face_visits', 'signed');
    case 'extraction':
      return tabStatusLabel(i, 'extraction');
    case 'eligibility':
      return tabStatusLabel(i, 'eligibility');
    case 'prior_auth':
      return tabStatusLabel(i, 'prior_auth');
    case 'ready_to_call':
      return tabStatusLabel(i, 'ready_to_call');
    case 'scheduling':
      return tabStatusLabel(i, 'scheduling');
    case 'push_to_emr':
      return tabStatusLabel(i, 'push_to_emr');
    case 'clinic':
      return i.agency_clinic?.name ?? '';
    case 'archived':
      return boolLabel(i.archived);
    case 'non_admit':
      return boolLabel(i.non_admit);
    case 'soc_date': {
      const soc = i.soc_date ?? i.kinnser_soc_date;
      return formatDateish(soc);
    }
    case 'discharge_date':
      return formatDateish(i.discharge_date);
    case 'created':
      return formatDateish(i.created_at);
    default:
      return '';
  }
}
